import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Truck, 
  FileCheck, 
  Calculator, 
  Phone, 
  MessageCircle,
  Send,
  Zap,
  Sun,
  Building2,
  ChevronDown,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';

export const Professionisti = () => {
  const { toast } = useToast();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    nomeAzienda: '',
    comune: '',
    kgStimati: '',
    contatto: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.nomeAzienda,
          email: formData.contatto.includes('@') ? formData.contatto : '',
          phone: !formData.contatto.includes('@') ? formData.contatto : '',
          message: `Richiesta quotazione per circa ${formData.kgStimati} di cavi elettrici`,
          source: 'professionisti',
          comune: formData.comune,
          kg_stimati: formData.kgStimati,
        }),
      });

      if (response.ok) {
        toast({
          title: 'Richiesta Inviata!',
          description: 'Ti ricontatteremo entro 24 ore con una quotazione personalizzata.',
        });
        setFormData({
          nomeAzienda: '',
          comune: '',
          kgStimati: '',
          contatto: '',
        });
        setPrivacyAccepted(false);
      } else {
        throw new Error('Errore durante l\'invio');
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore. Riprova più tardi.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vantaggi = [
    {
      icon: Truck,
      title: 'Ritiro Flessibile',
      description: 'Ritiro presso la tua sede per quantitativi adeguati, oppure consegna diretta presso il nostro impianto. Organizziamo la soluzione migliore per te.',
    },
    {
      icon: FileCheck,
      title: 'Formulari in Regola',
      description: 'Ti rilasciamo tutti i documenti necessari per la tracciabilità dei rifiuti. In caso di controlli ASL o Ambiente, sei completamente coperto.',
    },
    {
      icon: Calculator,
      title: 'Prezzo Chiaro e Stabile',
      description: 'Quotazione trasparente basata sulla tipologia e quantità del materiale.',
    },
  ];

  const steps = [
    {
      numero: '01',
      title: 'Contatto Iniziale',
      description: 'Richiedi una quotazione inviandoci alcune foto dei cavi e una stima del peso complessivo. Siamo disponibili via WhatsApp, email o telefono per rispondere ad ogni tua domanda.',
    },
    {
      numero: '02',
      title: 'Proposta Personalizzata',
      description: 'Ti mandiamo una proposta con modalità di ritiro o consegna, pesatura e condizioni economiche chiare in base ai quantitativi.',
    },
    {
      numero: '03',
      title: 'Ritiro o Consegna',
      description: 'Organizziamo insieme la modalità migliore: ritiro presso la tua sede (per quantitativi adeguati) o consegna presso il nostro impianto.',
    },
    {
      numero: '04',
      title: 'Documentazione e Pagamento',
      description: 'Ti rilasciamo formulari e documentazione completa. Ricevi il pagamento nei tempi concordati, senza ritardi.',
    },
  ];

  const comuni = [
    'Ivrea e comuni limitrofi',
    'Caluso, Chivasso, Rivarolo Canavese',
    'Torino e prima cintura',
    'Cuorgnè, Castellamonte, Strambino',
  ];

  const settori = [
    {
      icon: Zap,
      title: 'Per Elettricisti e Installatori',
      description: 'Libera il magazzino dai cavi di scarto. Niente più viaggi al rottamaio, documenti sempre in ordine, prezzo concordato prima del ritiro. Un unico referente per tutto.',
    },
    {
      icon: Sun,
      title: 'Per Installatori Fotovoltaico',
      description: 'Gestiamo i cavi dei tuoi cantieri fotovoltaici con report ambientali dettagliati (kg rame recuperato, CO₂ evitata). Dati concreti da mostrare ai tuoi clienti per rafforzare la tua immagine green.',
    },
    {
      icon: Building2,
      title: 'Per Aziende e Imprese Impiantistiche',
      description: 'Soluzioni strutturate per aziende, imprese di impianti e realtà con volumi importanti: ritiri programmati e periodici, gestione documentale completa e un referente unico per semplificare la vostra operatività. Ideale per cantieri, stabilimenti e imprese che generano cavi di scarto in modo continuativo.',
    },
  ];

  const faqs = [
    {
      domanda: 'Qual è il quantitativo minimo per il ritiro?',
      risposta: 'Il ritiro presso la tua sede dipende dalla quantità e dalla distanza. Contattaci per valutare insieme la soluzione migliore. Per consegne dirette presso il nostro impianto accettiamo qualsiasi quantità.',
    },
    {
      domanda: 'Quanto tempo ci vuole per avere il pagamento?',
      risposta: 'Il pagamento viene effettuato nei tempi concordati in fase di proposta, generalmente entro 7 giorni dalla pesatura e documentazione.',
    },
    {
      domanda: 'Che tipi di cavi ritirate?',
      risposta: 'Ritiriamo cavi elettrici di ogni tipo: civili, industriali, da fotovoltaico, con o senza connettori. Contattateci per materiali particolari.',
    },
    {
      domanda: 'Come viene calcolato il prezzo?',
      risposta: 'Il prezzo si basa sulla tipologia di cavo (stima % rame) e sul prezzo di mercato del rame. Ti facciamo sempre una quotazione chiara prima dell\'accordo.',
    },
  ];

  return (
    <div className="pt-20">
      {/* HERO SECTION */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] text-white overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <Zap className="h-4 w-4" />
              <span className="text-sm font-semibold">Servizio Dedicato</span>
            </div>
            
            {/* Titolo H1 */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in">
              Servizio Dedicato ai Professionisti
            </h1>
            
            {/* Sottotitolo */}
            <p className="text-lg md:text-xl mb-10 text-white/90 leading-relaxed max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Più tempo per i cantieri, meno magazzino pieno. Ritiro su misura, documentazione in regola e pagamento trasparente.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <Link to="/contatti">
                <Button
                  size="lg"
                  className="bg-white text-[var(--copper-primary)] hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Richiedi Quotazione Gratuita
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+390124572040">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[var(--copper-primary)] px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Chiamaci Ora
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE VANTAGGI */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Perché Scegliere FIRM per i Tuoi Cavi</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vantaggi.map((vantaggio, index) => {
              const Icon = vantaggio.icon;
              return (
                <Card
                  key={index}
                  className="group border-2 border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2 bg-white overflow-hidden"
                >
                  <CardContent className="p-8 relative">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[var(--copper-primary)]/5 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-700"></div>
                    
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-all duration-300">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-bold mb-4 group-hover:text-[var(--copper-primary)] transition-colors duration-300">
                        {vantaggio.title}
                      </h3>
                      <p className="text-[var(--text-light)] leading-relaxed">
                        {vantaggio.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEZIONE COME FUNZIONA */}
      <section className="py-20 md:py-24 bg-[var(--warm-gray)]">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Come Funziona il Servizio</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector Line (hidden on mobile and last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-[var(--copper-primary)]/20"></div>
                )}
                
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="w-14 h-14 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-full flex items-center justify-center mb-6 shadow-lg mx-auto lg:mx-0">
                    <span className="text-white font-bold text-lg">{step.numero}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-center lg:text-left">{step.title}</h3>
                  <p className="text-[var(--text-light)] leading-relaxed text-center lg:text-left">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE ZONA SERVITA */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Dove Operiamo</h2>
            </div>

            <div className="bg-gradient-to-br from-[var(--warm-gray)] to-white rounded-3xl p-8 md:p-12 shadow-lg">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-lg text-[var(--text-light)] leading-relaxed mb-6">
                    Serviamo elettricisti, installatori e imprese del Canavese e provincia di Torino, con sede operativa nel <strong className="text-[var(--text-dark)]">Canavese</strong>.
                  </p>
                  <p className="text-lg text-[var(--text-light)] leading-relaxed">
                    Ritiro possibile per quantitativi significativi nelle zone limitrofe. Per consegne dirette presso la nostra sede, accettiamo materiale di ogni quantità previo accordo.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <h4 className="font-semibold text-lg mb-2">Comuni principali serviti:</h4>
                <p className="text-[var(--text-light)]">
                  Operiamo principalmente a Torino e prima cintura, nel Canavese e nelle zone limitrofe della provincia di Torino; valutiamo interventi anche in aree più lontane su richiesta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE TARGET SPECIFICO */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Servizi Specializzati per Settore</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {settori.map((settore, index) => {
              const Icon = settore.icon;
              return (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{settore.title}</h3>
                    <p className="text-white/90 leading-relaxed mb-6">
                      {settore.description}
                    </p>
                    <Link to="/contatti" className="inline-flex items-center gap-2 text-white font-semibold hover:gap-3 transition-all duration-300">
                      Scopri di più
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEZIONE FAQ */}
      <section id="domande-frequenti" className="py-20 md:py-24 bg-white scroll-mt-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Domande Frequenti</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[var(--copper-primary)] transition-colors duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    <span className="font-semibold text-lg pr-4">{faq.domanda}</span>
                    <ChevronDown 
                      className={`h-5 w-5 text-[var(--copper-primary)] flex-shrink-0 transition-transform duration-300 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-5 pt-2 text-[var(--text-light)] leading-relaxed">
                      {faq.risposta}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE CTA FINALE */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Inizia Oggi a Valorizzare i Tuoi Cavi</h2>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                Contattaci per una valutazione gratuita del tuo materiale. Un nostro referente ti risponderà entro 24 ore.
              </p>
            </div>

            {/* Form */}
            <Card className="bg-white/10 backdrop-blur-sm border border-white/20 mb-10">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="nomeAzienda" className="text-white mb-2 block">Nome e Azienda *</Label>
                    <Input
                      id="nomeAzienda"
                      name="nomeAzienda"
                      type="text"
                      required
                      value={formData.nomeAzienda}
                      onChange={handleChange}
                      placeholder="Mario Rossi - Elettricista"
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="comune" className="text-white mb-2 block">Comune *</Label>
                    <Input
                      id="comune"
                      name="comune"
                      type="text"
                      required
                      value={formData.comune}
                      onChange={handleChange}
                      placeholder="Es. Ivrea, Torino..."
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="kgStimati" className="text-white mb-2 block">Kg Cavi Stimati *</Label>
                    <Input
                      id="kgStimati"
                      name="kgStimati"
                      type="text"
                      required
                      value={formData.kgStimati}
                      onChange={handleChange}
                      placeholder="Es. 50 kg, 200 kg..."
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contatto" className="text-white mb-2 block">Telefono o Email *</Label>
                    <Input
                      id="contatto"
                      name="contatto"
                      type="text"
                      required
                      value={formData.contatto}
                      onChange={handleChange}
                      placeholder="Come preferisci essere contattato"
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white"
                    />
                  </div>

                  <div className="md:col-span-2 flex items-start space-x-3">
                    <Checkbox
                      id="privacy-professionisti"
                      checked={privacyAccepted}
                      onCheckedChange={(checked) => setPrivacyAccepted(checked)}
                      className="mt-0.5 border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-[var(--copper-primary)] data-[state=checked]:border-white"
                      data-testid="privacy-checkbox-professionisti"
                    />
                    <Label htmlFor="privacy-professionisti" className="text-sm text-white/90 leading-relaxed cursor-pointer">
                      Ho letto e accetto la <a href="https://www.iubenda.com/privacy-policy/73606221" className="iubenda-white iubenda-noiframe iubenda-embed text-white underline font-medium hover:text-white/80" title="Privacy Policy">Privacy Policy</a> *
                    </Label>
                  </div>

                  <div className="md:col-span-2">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting || !privacyAccepted}
                      className="w-full bg-white text-[var(--copper-primary)] hover:bg-gray-100 py-6 text-lg font-semibold shadow-lg disabled:opacity-50"
                      data-testid="submit-professionisti"
                    >
                      {isSubmitting ? (
                        'Invio in corso...'
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Richiedi Quotazione
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Alternative Contact */}
            <div className="text-center">
              <p className="text-white/90 mb-6 text-lg">
                Preferisci parlare direttamente? Chiamaci o scrivici su WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://wa.me/390124572040" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Scrivici su WhatsApp
                  </Button>
                </a>
                <a href="tel:+390124572040">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-[var(--copper-primary)] px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Chiama Ora
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
